import { useCallback, useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

interface PWAStatus {
  isInstallable: boolean
  isOffline: boolean
  isInstalled: boolean
  installPrompt: (() => Promise<void>) | null
  needRefresh: boolean
  updateApp: (() => Promise<void>) | null
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWA(): PWAStatus {
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  const { needRefresh, updateServiceWorker } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        console.info('PWA service worker registered')
      }
    },
    onRegisterError(error) {
      console.error('PWA service worker registration failed', error)
    },
  })

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
      setIsInstallable(true)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const installPrompt = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    setDeferredPrompt(null)
    setIsInstallable(false)
  }, [deferredPrompt])

  const updateApp = useCallback(async () => {
    await updateServiceWorker(true)
  }, [updateServiceWorker])

  return {
    isInstallable,
    isOffline,
    isInstalled,
    installPrompt: isInstallable ? installPrompt : null,
    needRefresh: needRefresh[0],
    updateApp: needRefresh[0] ? updateApp : null,
  }
}

export function useBackgroundSync() {
  const sync = useCallback(async (tag: string) => {
    if ('serviceWorker' in navigator && 'sync' in window) {
      const registration = await navigator.serviceWorker.ready
      await (registration as any).sync.register(tag)
    }
  }, [])

  return { sync }
}
